import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  BellRing,
  CheckCircle2,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Settings2,
  ShieldCheck,
  Smartphone,
  UserCircle2,
} from 'lucide-react';
import { API, toast } from '@/App';
import ChangePasswordCard from '@/components/account/ChangePasswordCard';
import NotificationPreferencesSection from '@/components/account/NotificationPreferencesSection';
import SavedAddressesSection from '@/components/account/SavedAddressesSection';
import TwoFactorSettingsCard from '@/components/account/TwoFactorSettingsCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UI_NAV_LABELS, UI_PAGE_TITLES } from '@/constants/uiLabels';
import { clearAuthSession } from '@/utils/authSession';
import {
  formatAccountRoleLabel,
  resolveAccountBackLabel,
  resolveAccountBackPath,
} from '@/utils/accountNavigation';

const buildProfileForm = (user) => ({
  full_name: user?.full_name || '',
  email: user?.email || '',
  phone: user?.phone || '',
  city: user?.city || '',
});

const buildTwoFactorState = (source) => ({
  two_factor_enabled: Boolean(source?.two_factor_enabled),
  two_factor_method: source?.two_factor_enabled ? (source?.two_factor_method || 'email_otp') : null,
  two_factor_verified_at: source?.two_factor_verified_at || null,
});

const sectionItems = [
  { id: 'profile', label: 'Profil', icon: UserCircle2 },
  { id: 'security', label: 'Securite', icon: ShieldCheck },
  { id: 'addresses', label: 'Adresses enregistrees', icon: MapPin },
  { id: 'notifications', label: 'Notifications', icon: BellRing },
];

const isAmbassadorAccount = (role) => ['ambassador', 'super_ambassador'].includes(String(role || '').trim().toLowerCase());

const AccountSettingsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [twoFactorState, setTwoFactorState] = useState(buildTwoFactorState(null));
  const [profileForm, setProfileForm] = useState(buildProfileForm(null));
  const [loading, setLoading] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [profileNotice, setProfileNotice] = useState('');

  useEffect(() => {
    let active = true;

    const loadUser = async () => {
      setLoading(true);
      try {
        const [userResponse, twoFactorResponse] = await Promise.allSettled([
          axios.get(`${API}/auth/me`),
          axios.get(`${API}/account/2fa/status`),
        ]);

        if (userResponse.status !== 'fulfilled') {
          throw userResponse.reason;
        }

        if (!active) {
          return;
        }

        const nextUser = userResponse.value?.data || null;
        const nextTwoFactor =
          twoFactorResponse.status === 'fulfilled'
            ? buildTwoFactorState(twoFactorResponse.value?.data?.data?.two_factor)
            : buildTwoFactorState(nextUser);
        setUser(nextUser);
        setTwoFactorState(nextTwoFactor);
        setProfileForm(buildProfileForm(nextUser));
      } catch (error) {
        if (active) {
          toast.error(error.response?.data?.detail || 'Impossible de charger vos parametres de compte.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadUser();
    return () => {
      active = false;
    };
  }, []);

  const securityState = useMemo(() => {
    const hasVerifiedEmail = Boolean(user?.email_verified);
    const hasPhone = Boolean(String(user?.phone || '').trim());
    const hasTwoFactor = Boolean(twoFactorState?.two_factor_enabled);
    if (hasVerifiedEmail && hasPhone && hasTwoFactor) {
      return {
        label: 'Renforce',
        badgeClassName: 'bg-emerald-100 text-emerald-700',
        description: 'Votre compte combine email verifie, contact renseigne et verification en deux etapes pour les actions sensibles.',
      };
    }
    if (hasVerifiedEmail && hasPhone) {
      return {
        label: 'Bon niveau',
        badgeClassName: 'bg-emerald-100 text-emerald-700',
        description: 'Votre compte a deja ses bases de securite: email verifie et contact renseigne.',
      };
    }
    if (hasVerifiedEmail || hasPhone) {
      return {
        label: 'A renforcer',
        badgeClassName: 'bg-amber-100 text-amber-700',
        description: 'Le compte est utilisable, mais quelques etapes de securite restent a consolider.',
      };
    }
    return {
      label: 'A finaliser',
      badgeClassName: 'bg-slate-100 text-slate-700',
      description: 'Ajoutez vos informations de base et verifiez votre email pour mieux proteger le compte.',
    };
  }, [twoFactorState, user]);

  const showSavedAddresses = !isAmbassadorAccount(user?.role);
  const visibleSectionItems = showSavedAddresses
    ? sectionItems
    : sectionItems.filter((item) => item.id !== 'addresses');

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const scrollToSection = (sectionId) => {
    const target = document.getElementById(sectionId);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSaveProfile = async (event) => {
    event.preventDefault();
    const normalizedFullName = String(profileForm.full_name || '').trim();
    const normalizedEmail = String(profileForm.email || '').trim();
    if (!normalizedFullName || !normalizedEmail) {
      toast.error('Le nom complet et l email sont obligatoires.');
      return;
    }

    setSavingProfile(true);
    setProfileNotice('');
    try {
      const response = await axios.patch(`${API}/account/profile`, {
        full_name: normalizedFullName,
        email: normalizedEmail,
        phone: String(profileForm.phone || '').trim(),
        city: String(profileForm.city || '').trim(),
      });
      const updatedProfile = response.data?.data?.profile || {};
      const nextUser = {
        ...(user || {}),
        ...updatedProfile,
      };
      setUser(nextUser);
      setTwoFactorState((current) => buildTwoFactorState({ ...current, ...nextUser }));
      setProfileForm(buildProfileForm(nextUser));
      if (response.data?.data?.email_changed) {
        setProfileNotice('Votre nouvel email doit etre confirme avant les prochaines actions sensibles.');
      }
      window.dispatchEvent(new Event('cnk-auth-refresh'));
      toast.success(response.data?.message || 'Profil mis a jour.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Email already registered') {
        toast.error('Cette adresse email est deja utilisee.');
      } else {
        toast.error(detail || 'Impossible de mettre a jour votre profil.');
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const response = await axios.post(`${API}/auth/resend-verification`);
      toast.success(response.data?.message || 'Email de verification renvoye.');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible de renvoyer l email de verification.");
    } finally {
      setResendingVerification(false);
    }
  };

  const handlePasswordChanged = () => {
    clearAuthSession();
    window.dispatchEvent(new Event('cnk-auth-refresh'));
    navigate('/');
  };

  const handleTwoFactorStateChange = (nextState) => {
    const normalizedState = buildTwoFactorState(nextState);
    setTwoFactorState(normalizedState);
    setUser((current) => (current ? { ...current, ...normalizedState } : current));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto" />
          <p className="mt-4 text-gray-600">Chargement de vos parametres...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm font-medium text-red-600">{UI_NAV_LABELS.settings}</p>
            <h1 className="text-3xl font-bold text-gray-900">{UI_PAGE_TITLES.accountSettings}</h1>
            <p className="mt-2 max-w-3xl text-sm text-gray-600">
              Centralisez votre profil, la securite du compte et les preferences de notifications.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate(resolveAccountBackPath(user?.role))}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {resolveAccountBackLabel(user?.role)}
          </Button>
        </div>

        <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="space-y-6 xl:sticky xl:top-6 self-start">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-red-600" />
                  Centre du compte
                </CardTitle>
                <CardDescription>
                  Un point unique pour vos informations personnelles et vos reglages essentiels.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-base font-semibold text-slate-900">{user?.full_name || 'Utilisateur'}</p>
                  <p className="mt-1 text-sm text-slate-600">{user?.email || 'Email indisponible'}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{formatAccountRoleLabel(user?.role)}</Badge>
                    <Badge className={user?.email_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                      {user?.email_verified ? 'Email verifie' : 'Email a verifier'}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2">
                  {visibleSectionItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Button
                        key={item.id}
                        type="button"
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => scrollToSection(item.id)}
                      >
                        <Icon className="mr-2 h-4 w-4 text-red-600" />
                        {item.label}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-red-600" />
                  Etat de securite
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Badge className={securityState.badgeClassName}>{securityState.label}</Badge>
                <p className="text-sm text-slate-600">{securityState.description}</p>
                <div className="space-y-2 text-sm text-slate-700">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-red-600" />
                    <span>{user?.email_verified ? 'Email confirme' : 'Email non confirme'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-4 w-4 text-red-600" />
                    <span>{String(user?.phone || '').trim() ? 'Telephone renseigne' : 'Telephone non renseigne'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-red-600" />
                    <span>{twoFactorState?.two_factor_enabled ? '2FA email OTP active' : '2FA non activee'}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>

          <div className="space-y-8">
            <section id="profile" data-testid="settings-profile-section" className="scroll-mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCircle2 className="h-5 w-5 text-red-600" />
                    Profil
                  </CardTitle>
                  <CardDescription>
                    Mettez a jour vos informations de base sans casser l authentification existante.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form className="space-y-6" onSubmit={handleSaveProfile}>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="settings_full_name">Nom complet</Label>
                        <Input
                          id="settings_full_name"
                          name="full_name"
                          value={profileForm.full_name}
                          onChange={handleProfileChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="settings_email">Email</Label>
                        <Input
                          id="settings_email"
                          name="email"
                          type="email"
                          value={profileForm.email}
                          onChange={handleProfileChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="settings_phone">Telephone</Label>
                        <Input
                          id="settings_phone"
                          name="phone"
                          value={profileForm.phone}
                          onChange={handleProfileChange}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="settings_city">Ville</Label>
                        <Input
                          id="settings_city"
                          name="city"
                          value={profileForm.city}
                          onChange={handleProfileChange}
                        />
                      </div>
                    </div>

                    <div className="grid gap-4 md:grid-cols-[1fr_auto]">
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant="outline">{formatAccountRoleLabel(user?.role)}</Badge>
                          <Badge className={user?.email_verified ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                            {user?.email_verified ? 'Email verifie' : 'Email non verifie'}
                          </Badge>
                        </div>
                        <p className="mt-3 text-sm text-slate-700">
                          Si vous modifiez votre email, une nouvelle verification sera demandee avant les prochaines actions sensibles.
                        </p>
                        {profileNotice && (
                          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                            {profileNotice}
                          </div>
                        )}
                      </div>

                      {!user?.email_verified && (
                        <div className="flex items-start">
                          <Button type="button" variant="outline" onClick={handleResendVerification} disabled={resendingVerification}>
                            {resendingVerification ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                            {resendingVerification ? 'Envoi...' : 'Renvoyer email de verification'}
                          </Button>
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit" className="bg-red-500 hover:bg-red-600" disabled={savingProfile}>
                        {savingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                        {savingProfile ? 'Enregistrement...' : 'Enregistrer le profil'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </section>

            <section id="security" data-testid="settings-security-section" className="scroll-mt-6">
              <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
                <ChangePasswordCard onPasswordChanged={handlePasswordChanged} twoFactorState={twoFactorState} />

                <div className="space-y-6">
                  <TwoFactorSettingsCard
                    twoFactorState={twoFactorState}
                    emailVerified={Boolean(user?.email_verified)}
                    onStatusChange={handleTwoFactorStateChange}
                  />

                  <Card>
                    <CardHeader>
                      <CardTitle>Etat du compte</CardTitle>
                      <CardDescription>Resume simple pour savoir ce qui est deja solide et ce qui arrive ensuite.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm text-slate-700">
                      <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
                        <Mail className="mt-0.5 h-4 w-4 text-red-600" />
                        <div>
                          <p className="font-medium text-slate-900">Verification email</p>
                          <p>{user?.email_verified ? 'Activee et confirmee.' : 'Encore necessaire pour certaines actions sensibles.'}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
                        <KeyRound className="mt-0.5 h-4 w-4 text-red-600" />
                        <div>
                          <p className="font-medium text-slate-900">Mot de passe</p>
                          <p>Le changement en session est disponible ici. Le flow forgot/reset reste disponible pour recuperer l acces.</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 rounded-xl border border-slate-200 p-4">
                        <ShieldCheck className="mt-0.5 h-4 w-4 text-red-600" />
                        <div>
                          <p className="font-medium text-slate-900">Verification en deux etapes</p>
                          <p>
                            {twoFactorState?.two_factor_enabled
                              ? 'Activee via email OTP pour les actions sensibles deja branchees.'
                              : 'Disponible via email OTP. La meme base pourra ensuite couvrir d autres actions sensibles.'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </section>

            {showSavedAddresses && (
              <section id="addresses" data-testid="settings-addresses-section" className="scroll-mt-6">
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-slate-900">Adresses enregistrees</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Cette section reutilise le module existant d adresses sauvegardees, sans systeme parallele.
                  </p>
                </div>
                <SavedAddressesSection />
              </section>
            )}

            <section id="notifications" data-testid="settings-notifications-section" className="scroll-mt-6">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-slate-900">Notifications</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Les preferences restent stockees cote backend et continuent d alimenter les automations existantes.
                </p>
              </div>
              <NotificationPreferencesSection />
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AccountSettingsPage;
